import './TubeSheetGraph.scss';

import { useIonViewDidLeave } from '@ionic/react';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { selectIsMetric } from 'src/redux/profile-preferences/selectors';
import { assertNever } from 'src/utils/assert';
import { JobLogV2Data } from 'src/utils/logs/types';
import { TubeSheetGraphKey } from './TubeSheetGraphKey';
import { TubeSheetGraphController } from './util/TubeSheetGraphController';
import { TubeSheetGraphTranslator } from './util/TubeSheetGraphTranslator';
import { TubeSheetChartType, TubeSheetGraphDataElement } from './util/types';

export interface TubeSheetGraphProps {
  /** A list of segments of the tube sheet log. */
  tubeSheet: JobLogV2Data['tubeSheet'];
  /** The time table used to correlate events with their calendary time. */
  timeTable: JobLogV2Data['timeTable'];
  /** The firmware version that generated the log file. */
  firmware: JobLogV2Data['firmwareVersion'] | undefined;
  /** Time range min value */
  timeRangeMin: number;
  /** Time range max value */
  timeRangeMax: number;
  /** The timezone associated with the job log. */
  timezone: string;
  /** Auto move events */
  autoMove: JobLogV2Data['configuration']['autoMove'];
  /** Whether to hide timestamps */
  showTimeDetails: boolean;
  /** The type of chart being displayed. */
  chartType: TubeSheetChartType;
}

const mapChartTypeToClassName = (chart: TubeSheetChartType) => {
  switch (chart) {
    case TubeSheetChartType.MAIN:
      return 'tube-sheet-graph-main';
    case TubeSheetChartType.HEAT:
      return 'tube-sheet-graph-heat';
    case TubeSheetChartType.DEPTH:
      return 'tube-sheet-graph-depth';
    case TubeSheetChartType.AUTOSTROKES:
      return 'tube-sheet-graph-autostroke';
    case TubeSheetChartType.LANCESPEED:
      return 'tube-sheet-graph-lance-speed';
    default:
      assertNever(chart);
  }
};

const TubeSheetGraph: React.FC<TubeSheetGraphProps> = props => {
  const intl = useIntl();
  const isMetric = useSelector(selectIsMetric);
  /** Ref object that contains the element under which the chart is created. */
  const graphRef = React.useRef<SVGSVGElement>(null);
  /** The controller object managing the svg. */
  const [ctrlRef, setCtrlRef] = useState<TubeSheetGraphController | null>(null);

  const getTooltipForItem = useCallback(
    (item: TubeSheetGraphDataElement): string[] => {
      switch (props.chartType) {
        case TubeSheetChartType.MAIN:
        case TubeSheetChartType.AUTOSTROKES:
        case TubeSheetChartType.DEPTH: {
          const result = [];
          if (item.description.depth) result.push(item.description.depth);
          if (item.description.autoStroke) result.push(...item.description.autoStroke);
          return result;
        }
        case TubeSheetChartType.HEAT: {
          const result = [];
          if (item.description.duration) result.push(item.description.duration);
          return result;
        }
        case TubeSheetChartType.LANCESPEED: {
          const result = [];
          if (item.description.lanceSpeed) result.push(item.description.lanceSpeed);
          return result;
        }
        default:
          assertNever(props.chartType);
      }
    },
    [props.chartType],
  );

  // Effect that creates the svg graph element on mount, and destroys it on unmount.
  useEffect(() => {
    const graph = graphRef.current;
    if (!graph) {
      console.warn('The component mounted but graphRef was null! The graph will not draw.');
      return;
    }

    const controller = new TubeSheetGraphController(graph);
    controller.setTooltipAccessor(getTooltipForItem);
    setCtrlRef(controller);

    return () => {
      controller.setTooltipAccessor(() => []);
    };
  }, [getTooltipForItem]);

  useIonViewDidLeave(() => {
    ctrlRef?.reset();
  }, [ctrlRef]);

  const graphTranslator = React.useMemo(() => {
    return new TubeSheetGraphTranslator({
      intl,
      isMetric,
      showTimeDetails: props.showTimeDetails,
      timezone: props.timezone,
    });
  }, [intl, isMetric, props.showTimeDetails, props.timezone]);

  /** The view model data for the tube sheet graph. */
  const graphData = React.useMemo(() => {
    if (!props.tubeSheet) return undefined;

    const parsedData = graphTranslator.parseDataV2(
      props.tubeSheet,
      props.timeTable,
      props.autoMove,
      props.firmware,
      props.timeRangeMin,
      props.timeRangeMax,
    );

    ctrlRef?.setData(props.chartType, parsedData);

    return {
      data: parsedData,
      chartType: props.chartType,
    };
  }, [
    props.tubeSheet,
    props.timeTable,
    props.autoMove,
    props.firmware,
    props.timeRangeMin,
    props.timeRangeMax,
    props.chartType,
    graphTranslator,
    ctrlRef,
  ]);

  return (
    <div className={classNames('tube-sheet-graph', mapChartTypeToClassName(props.chartType))}>
      <div className="tube-sheet-graph-content">
        <svg ref={graphRef} />
      </div>
      <TubeSheetGraphKey
        chartType={props.chartType}
        showTimeDetails={props.showTimeDetails}
        isMetric={isMetric}
        breakpoints={graphData?.data.breakpoints}
      />
    </div>
  );
};

export default TubeSheetGraph;
